import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { API_BASE_URL } from "../../utils/constants";
import DatePicker from "react-datepicker";
import "bootstrap/dist/css/bootstrap.min.css";
import ClearForm from "../../components/Clearform";
import CaseJourneyTimeline from "../../components/CaseJourneyTimeline";

const STAGES = [
  { stage_id: 1, stage_name: "Upload Date" },
  { stage_id: 2, stage_name: "Arbitrator Assigned" },
  { stage_id: 3, stage_name: "Appointment" },
  { stage_id: 4, stage_name: "Acceptance" },
  { stage_id: 5, stage_name: "SOC" },
  { stage_id: 6, stage_name: "Sec 17 Application" },
  { stage_id: 7, stage_name: "Sec 17 Order / First Hearing" },
  { stage_id: 8, stage_name: "Second Hearing" },
  { stage_id: 9, stage_name: "Final Hearing" },
  { stage_id: 10, stage_name: "Pursis" },
  { stage_id: 11, stage_name: "Award" },
];

const HoldCase = () => {
  const [loading, setLoading] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState("");
  const [refNo, setRefNo] = useState("");
  const [data, setData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [formattedDate, setFormattedDate] = useState(null);
  const [selectedStageID, setSelectedStageID] = useState(null);
  const [holdReason, setHoldReason] = useState("");
  const [clearForm, setClearForm] = useState(false);
  const [clearMessage, setClearMessage] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!refNo) return;
    const fetchRefInfo = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/Case?Ref_no=${refNo}`);
        if (!res.ok) throw new Error(await res.text());
        const result = await res.json();
        setData(Array.isArray(result) ? result : JSON.parse(result));
      } catch (err) {
        alert(`Error fetching case: ${err.message}`);
      }
    };
    fetchRefInfo();
  }, [refNo, refreshKey]);

  const customer = data.filter((d) => d.Type === "B");
  const c = customer[0];
  const isOnHold = c?.Is_hold === "1";

  const handleHold = async () => {
    if (!c) return;
    if (!holdReason.trim()) { alert("Please enter a hold reason."); return; }
    if (!selectedStageID) { alert("Please select a hold stage."); return; }
    if (!formattedDate) { alert("Please select a hold date."); return; }

    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/hold`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          caseId: c.Case_id,
          holdReason,
          holdStageId: parseInt(selectedStageID, 10),
          holdDate: formattedDate,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setSuccessMsg("Case put on Hold Successfully!");
      setHoldReason("");
      setSelectedDate(null);
      setFormattedDate(null);
      setSelectedStageID(null);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRelease = async () => {
    if (!c) return;
    if (!window.confirm("Release this case from Hold? It will re-enter the active flow.")) return;
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/unhold`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ caseId: c.Case_id }),
      });
      if (!res.ok) throw new Error(await res.text());
      setClearMessage("Case Released from Hold Successfully!");
      setClearForm(true);
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (clearForm) {
    return (
      <div className="row">
        <div className="col-md-12 d-flex justify-content-center">
          <ClearForm message={clearMessage} redirectPath="/arbdashboard" />
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-3 py-2" style={{ maxWidth: "1400px", margin: "0 auto" }}>
      {/* Header */}
      <div className="mb-3">
        <h5 className="mb-1" style={{ fontWeight: 600, color: "#2c3e50", fontSize: "1.1rem" }}>Hold Management</h5>
        <small className="text-muted" style={{ fontSize: "0.8rem" }}>Search and manage case hold status</small>
      </div>

      {/* Search Card */}
      <div className="card shadow-sm border-0 mb-3">
        <div className="card-body p-3">
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div style={{ flex: "1", minWidth: "280px", maxWidth: "450px" }}>
              <input
                type="text"
                className="form-control"
                placeholder="Enter Reference Number"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (setSuccessMsg(""), setRefNo(referenceNumber))}
                style={{ fontSize: "0.85rem", borderRadius: "6px" }}
              />
            </div>
            <button
              className="custBtn"
              onClick={() => { setSuccessMsg(""); setRefNo(referenceNumber); }}
              style={{ padding: "8px 24px", fontSize: "0.85rem" }}
            >
              Search
            </button>
            {isOnHold && (
              <div
                style={{
                  background: "#fff3cd",
                  color: "#856404",
                  padding: "6px 14px",
                  borderRadius: "6px",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  border: "1px solid #ffc107",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}
              >
                ⏸ On Hold
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMsg && (
        <div className="alert alert-success border-0 shadow-sm mb-3 py-2" style={{ fontSize: "0.85rem", borderRadius: "6px" }}>
          ✓ {successMsg}
        </div>
      )}

      {c && (
        <>
          {/* Case Journey - Side by Side if Contested */}
          {c.Contested_date ? (
            <div className="row g-2 mb-2">
              {/* Regular Case Journey */}
              <div className="col-lg-6">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body p-3">
                    <h6 className="mb-2" style={{ fontSize: "0.9rem", fontWeight: 600, color: "#2c3e50" }}>
                      Case Journey Timeline
                    </h6>
                    <CaseJourneyTimeline caseData={c} showRegularOnly={true} />
                  </div>
                </div>
              </div>

              {/* Contested Case Journey */}
              <div className="col-lg-6">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body p-3">
                    <h6 className="mb-2" style={{ fontSize: "0.9rem", fontWeight: 600, color: "#2c3e50" }}>
                      Contested Case Journey
                    </h6>
                    <CaseJourneyTimeline caseData={c} showContestedOnly={true} />
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card shadow-sm border-0 mb-2">
              <div className="card-body p-3">
                <h6 className="mb-2" style={{ fontSize: "0.9rem", fontWeight: 600, color: "#2c3e50" }}>
                  Case Journey Timeline
                </h6>
                <CaseJourneyTimeline caseData={c} />
              </div>
            </div>
          )}

          {/* Two Column Layout */}
          <div className="row g-2 mb-2">
            {/* Left Section - Customer & Account Details */}
            <div className="col-lg-6">
              {/* Customer Details Card */}
              <div className="card shadow-sm border-0 mb-2">
                <div className="card-body p-3">
                  <h6 className="mb-2" style={{ fontSize: "0.9rem", fontWeight: 600, color: "#2c3e50" }}>
                    Customer Details
                  </h6>
                  <div className="row g-0">
                    {[
                      ["Reference No", c.Reference_no],
                      ["Customer Name", c.Cust_name],
                      ["Customer ID", c.Cust_id],
                      ["Mobile No", c.Mobile_no],
                      ["Email", c.Email_id],
                    ].map(([label, val]) => (
                      <div className="col-12" key={label}>
                        <div className="d-flex py-1" style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <span className="text-muted" style={{ fontSize: "0.8rem", width: "130px" }}>{label}</span>
                          <span style={{ fontSize: "0.8rem", fontWeight: 500, color: "#2c3e50" }}>{val || "—"}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Account Details Card */}
              <div className="card shadow-sm border-0">
                <div className="card-body p-3">
                  <h6 className="mb-2" style={{ fontSize: "0.9rem", fontWeight: 600, color: "#2c3e50" }}>
                    Account Details
                  </h6>
                  <div className="row g-0">
                    {[
                      ["Lot No", c.Lot_no],
                      ["Account No", c.Acc_no],
                      ["Client", c.client_name],
                      ["Product", c.Product_name],
                    ].map(([label, val]) => (
                      <div className="col-12" key={label}>
                        <div className="d-flex py-1" style={{ borderBottom: "1px solid #f0f0f0" }}>
                          <span className="text-muted" style={{ fontSize: "0.8rem", width: "130px" }}>{label}</span>
                          <span style={{ fontSize: "0.8rem", fontWeight: 500, color: "#2c3e50" }}>{val || "—"}</span>
                        </div>
                      </div>
                    ))}
                    {isOnHold && (
                      <>
                        <div className="col-12">
                          <div className="d-flex py-1" style={{ borderBottom: "1px solid #f0f0f0" }}>
                            <span className="text-muted" style={{ fontSize: "0.8rem", width: "130px" }}>Hold Stage</span>
                            <span style={{ fontSize: "0.8rem", fontWeight: 500, color: "#2c3e50" }}>
                              {STAGES.find((s) => s.stage_id === c.Hold_stage_id)?.stage_name || c.Hold_stage_id}
                            </span>
                          </div>
                        </div>
                        <div className="col-12">
                          <div className="d-flex py-1">
                            <span className="text-muted" style={{ fontSize: "0.8rem", width: "130px" }}>Hold Reason</span>
                            <span style={{ fontSize: "0.8rem", fontWeight: 500, color: "#2c3e50" }}>{c.Hold_reason}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Section - Action Section */}
            <div className="col-lg-6">
              {!isOnHold ? (
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body p-3">
                    <h6 className="mb-2" style={{ fontSize: "0.9rem", fontWeight: 600, color: "#2c3e50" }}>
                      Put Case on Hold
                    </h6>
                    <div className="row g-2">
                      <div className="col-12">
                        <label className="form-label mb-1" style={{ fontSize: "0.8rem", fontWeight: 500 }}>Hold Reason</label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          placeholder="Enter the reason for putting this case on hold..."
                          value={holdReason}
                          onChange={(e) => setHoldReason(e.target.value)}
                          style={{ fontSize: "0.8rem", borderRadius: "6px" }}
                        />
                      </div>
                      <div className="col-12">
                        <div className="d-flex align-items-center gap-2">
                          <label className="form-label mb-0" style={{ fontSize: "0.8rem", fontWeight: 500, whiteSpace: "nowrap" }}>Hold Date</label>
                          <DatePicker
                            selected={selectedDate}
                            onChange={(date) => {
                              setSelectedDate(date);
                              setFormattedDate(date ? date.toLocaleDateString("en-US") : "");
                            }}
                            placeholderText="Select date"
                            dateFormat="MM/dd/yyyy"
                            className="form-control"
                            style={{ fontSize: "0.8rem", borderRadius: "6px" }}
                          />
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="d-flex align-items-center gap-2">
                          <label className="form-label mb-0" style={{ fontSize: "0.8rem", fontWeight: 500, whiteSpace: "nowrap" }}>Hold Stage</label>
                          <Form.Select
                            onChange={(e) => setSelectedStageID(e.target.value)}
                            style={{ fontSize: "0.8rem", borderRadius: "6px", flex: 1 }}
                          >
                            <option value="">Select Stage</option>
                            {STAGES.map((s) => (
                              <option key={s.stage_id} value={s.stage_id}>{s.stage_name}</option>
                            ))}
                          </Form.Select>
                        </div>
                      </div>
                      <div className="col-12 mt-2">
                        <button
                          className="custBtn w-100"
                          onClick={handleHold}
                          disabled={loading}
                          style={{ padding: "8px", fontSize: "0.85rem", borderRadius: "6px" }}
                        >
                          {loading ? "Processing..." : "Put on Hold"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="card border-0 shadow-sm h-100"
                  style={{ background: "linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%)" }}
                >
                  <div className="card-body p-3 d-flex flex-column justify-content-center">
                    <div className="text-center">
                      <h6 className="mb-2" style={{ fontSize: "0.9rem", fontWeight: 600, color: "#856404" }}>
                        ⏸ Case on Hold
                      </h6>
                      <small style={{ color: "#856404", fontSize: "0.8rem" }}>
                        This case is currently suspended from the active workflow
                      </small>
                      <div className="mt-3">
                        <button
                          className="custBtn"
                          onClick={handleRelease}
                          disabled={loading}
                          style={{
                            background: "#198754",
                            padding: "8px 24px",
                            fontSize: "0.85rem",
                            borderRadius: "6px"
                          }}
                        >
                          {loading ? "Processing..." : "Release from Hold"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default HoldCase;
